import { autoinject, transient } from "aurelia-dependency-injection";
import { PermissionEntity } from "../../../../models/manage/permissions.models";
import { UnitOfWorkObserver } from "../../../../utilities/unit-of-work-observer";
import { ManagePermissionsClientService } from "../../../../services/manage/manage-permissions.service";
import { NotificationClientService, NotificationType } from "../../../../services/basics/notification-client.service";
import { PermissionModel, ObjectPermissionModel } from "../../../../models/manage/permissions.models";
import { BindingEngine } from "aurelia-binding";
import * as Enumerable from "linq";
import { UserClaimsService } from "../../../../services/authentication/user-claims.service";
import { BindingSignaler } from "aurelia-templating-resources";
import { ArrayUtils } from "../../../../utilities/arrays";
import { LookupClientService } from "../../../../services/lookups/lookup-client.service";
import { Router } from "aurelia-router";
import { computedFrom } from "aurelia-binding";
import { UserClientService } from "../../../../services/authentication/user-client.service";
import { Reflection } from "../../../../utilities/reflection";
import { I18N } from 'aurelia-i18n';
import { BaseClaims } from "../../../../constants/authentication/base-claims";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { DialogServiceConstants } from "../../../../constants/basics/dialog.constants";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { observable } from "aurelia-framework";

@autoinject
@transient()
export class Permissions {

    private _searchText: string;
    //used to operate cancel operation from another class(like in manage users)
    private _cancelOperation?: () => void = null;
    private _afterSaveOperation?: () => void = null;
    private permissionEntityType: EntityTypes;

    public type: string;
    public entityName: string;
    public property: string;
    public typeId: number;
    public id: number;
    public employeeIds: number[];
    public roleIds: number[];
    public departmentIds: number[];
    public branchIds: number[];
    public entity: any;
    public permissionsUow: UnitOfWorkObserver;
    public allPermissions: Enumerable.IGrouping<string, PermissionModel>[];
    public canAccessPermissions = true;
    public canUpdatePermissions: boolean = true;
    public showCloseButton: boolean = false;
    public infoIcons: HTMLElement[] = [];

    @observable
    public hideSelectedPermissions: boolean = false;

    @observable
    public hideUnSelectedPermissions: boolean = false;

    constructor(private managePermissionsService: ManagePermissionsClientService,
        private engine: BindingEngine,
        private lookupsService: LookupClientService,
        private router: Router,
        private bindingSignaler: BindingSignaler,
        private userClientService: UserClientService,
        private userClaimsService: UserClaimsService,
        private notificationService: NotificationClientService,
        private i18N: I18N,
        private dialogClientService: DialogClientService) {
        this.canAccessPermissions = this.userClaimsService.hasPermission(BaseClaims.CanAccessPermissions);
        this.canUpdatePermissions = this.userClaimsService.hasPermission(BaseClaims.CanUpdatePermissions);
    }

    public set searchText(value: string) {
        this._searchText = value;
        setTimeout(() => {
            this.bindingSignaler.signal('filter-permissions');
        });
    }

    @computedFrom(Reflection.getPropertyPath<Permissions>(x => x._searchText))
    public get searchText(): string {
        return this._searchText;
    }

    public activate(model): void {
        model.type = Number(model.type);
        this.typeId = model.type;
        this.id = model.id;
        this.entityName = model.name;
        this.employeeIds = model.employeeIds;
        this.roleIds = model.roleIds;
        this.departmentIds = model.departmentIds;
        this.branchIds = model.branchIds;
        this.showCloseButton = model.showCloseButton != null ? model.showCloseButton : false;
        this._cancelOperation = model.cancelOperation != null ? model.cancelOperation : null;
        this._afterSaveOperation = model.afterSaveOperation != null ? model.afterSaveOperation : null;
        if (model.type === PermissionEntity.Branch) {
            this.type = this.i18N.tr('branch-text');
            this.property = "branchPermissions";
            this.permissionEntityType = EntityTypes.BranchPermission;
        } else if (model.type === PermissionEntity.Department) {
            this.type = this.i18N.tr('department-text');
            this.property = "departmentPermissions";
            this.permissionEntityType = EntityTypes.DepartmentPermission;
        } else if (model.type === PermissionEntity.Role) {
            this.type = this.i18N.tr('role-text');
            this.property = "rolePermissions";
            this.permissionEntityType = EntityTypes.RolePermission;
        } else if (model.type === PermissionEntity.User) {
            this.type = this.i18N.tr('user-text');
            this.property = "userPermissions";
            this.permissionEntityType = EntityTypes.UserPermission;
        }
        this.getAllPermissions();
    }

    public getAllPermissions(): void {
        if (this.permissionsUow) {
            this.permissionsUow.dispose();
        }

        setTimeout(() => {
            this.managePermissionsService.getAllPermissions({ permissionEntity: this.typeId, id: this.id, branchIds: this.branchIds, departmentIds: this.departmentIds, roleIds: this.roleIds, employeeIds: this.employeeIds }).then(response => {

                // make sure for each level the permissions are not undefined or null by returning an empty array in case
                response.branchPermissions = this.getPermissionsOrEmpty(response.branchPermissions);
                response.rolePermissions = this.getPermissionsOrEmpty(response.rolePermissions);
                response.employeePermissions = this.getPermissionsOrEmpty(response.employeePermissions);
                response.departmentPermissions = this.getPermissionsOrEmpty(response.departmentPermissions);

                for (const per of response.permissions) {
                    per.isChecked = response.currentPermissions.some(x => x.permissionId == per.dbId);

                    const isBranchPermission = response.branchPermissions.some(x => x.permissionId == per.dbId);
                    const isDepartmentPermission = response.departmentPermissions.some(x => x.permissionId == per.dbId);
                    const isRolePermission = response.rolePermissions.some(x => x.permissionId == per.dbId);
                    const isEmployeePermission = response.employeePermissions.some(x => x.permissionId == per.dbId);

                    switch (this.type) {
                        case "Branch":
                            break;
                        case "Department":
                            if (isBranchPermission) {
                                per.isDisabled = per.isChecked = true;
                            }
                            break;
                        case "Role":
                            if (isBranchPermission || isDepartmentPermission) {
                                per.isDisabled = per.isChecked = true;
                            }
                            break;
                        case "User":
                            if (isBranchPermission || isDepartmentPermission || isRolePermission) {
                                per.isDisabled = per.isChecked = true;
                            }
                            break;
                        default:
                            if (isBranchPermission || isDepartmentPermission || isRolePermission || isEmployeePermission) {
                                per.isDisabled = per.isChecked = true;
                            }
                            break;
                    }
                }
                const allPermissions = Enumerable.from<PermissionModel>(response.permissions).where(x => x.permissionCategory != null && x.permissionCategory.permissionType != null && !x.isHidden && !x.permissionCategory.isHidden && !x.permissionCategory.permissionType.isHidden)
                    .orderBy(x => x.permissionCategory.sequence)
                    .groupBy(x => x.permissionCategory.permissionType.description, x => x)
                    .toArray();

                this.allPermissions = allPermissions;

                this.entity = {};
                this.entity[this.property] = this.getMappedPermissions(response.currentPermissions)
                    .concat(this.getMappedPermissions(response.branchPermissions))
                    .concat(this.getMappedPermissions(response.departmentPermissions))
                    .concat(this.getMappedPermissions(response.rolePermissions))
                    .concat(this.getMappedPermissions(response.employeePermissions));

                this.entity[this.property] = this.entity[this.property].filter(function (item1, index, inputArray) {
                    return ArrayUtils.findIndex(inputArray, (item2) => item1.permissionId == item2.permissionId && item1.id == item2.id) == index;
                });
                this.searchText = null;
                setTimeout(() => {
                    this.permissionsUow = UnitOfWorkObserver.getTrackingInstance(this.engine, this.entity, []);
                    this.searchText = '';
                });
            },  () => { });
        });
    }

    private getPermissionsOrEmpty(arr: ObjectPermissionModel[]): ObjectPermissionModel[] {
        return arr ? arr : [];
    }

    private getMappedPermissions(permissions: ObjectPermissionModel[]) {
        return permissions.map(x => ({
            permissionId: x.permissionId,
            id: x.dbId
        }));
    }

    public goupByCategory(permissions: PermissionModel[]): Enumerable.IGrouping<string, PermissionModel>[] {

        const searchText = this.searchText ? this.searchText.toLowerCase() : '';
            permissions = permissions.filter(y=>(y.description.toLowerCase().indexOf(searchText) > -1 ||
            y.permissionCategory.description.toLowerCase().indexOf(searchText) > -1 ||
            y.permissionCategory.permissionType.description.toLowerCase().indexOf(searchText) > -1) &&
            (!this.hideSelectedPermissions || !y.isChecked) && (!this.hideUnSelectedPermissions || y.isChecked));

        const result = Enumerable.from(permissions).where(x => x.permissionCategory != null)
            .groupBy(x => x.permissionCategory.description, x => x)
            .toArray();     
        return result;
    }
    
    public groupItemsCheckboxItemsByDescription(permissions: PermissionModel[]) {
        return Enumerable.from(permissions).orderByDescending(x => x.description).toArray();
    }

    public goBack(): void {
        if (this._cancelOperation) {
            this._cancelOperation();
        }
        else {
            this.router.navigateBack();
        }
    }

    public areThereUnsavedChanges() {
        return this.permissionsUow.getHasChanges;
    }

    public assign(item: PermissionModel, updateGroup: boolean = true): void {
        setTimeout(() => {
            const permission = this.entity[this.property].filter(x => x.permissionId == item.dbId)[0];
            if (permission) {
                const index = this.entity[this.property].indexOf(permission);
                this.entity[this.property].splice(index, 1);
                item.isChecked = false;
            } else {
                this.entity[this.property].push({ permissionId: item.dbId });
                item.isChecked = true;
            }

            if (updateGroup) {
                setTimeout(() => {
                    this.bindingSignaler.signal(item.permissionCategory.description);
                });
            }
        });

    }

    public save(): Promise<void> {
        return new Promise<void>((resolve) => {
            if (!this.permissionsUow.hasChanges()) {
                this.notificationService.show(this.i18N.tr('no-changes-performed-text'), "", NotificationType.Info);
                resolve();
                return;
            }

            const func = () => {
                if (this._afterSaveOperation) {
                    this._afterSaveOperation();
                }

                if (this.userClaimsService.hasPermission(BaseClaims.CanAccessPermissions)) {
                    this.getAllPermissions();
                }

                this.notificationService.show(this.i18N.tr('successfully-saved-text'), this.i18N.tr('permission-has-been-successfully-saved-text'), NotificationType.Success);

                this.lookupsService.clearLookupCache("Branches").then(() => {
                    resolve();
                }).catch(() => {
                    resolve();
                });
            };

            this.managePermissionsService.save({
                permissionEntity: this.typeId,
                id: this.id,
                changes: this.permissionsUow.getChanges()
            }).then(() => {
                if (this.userClientService.currentUser.roleId === this.id) {
                    this.userClientService.reloadUser().then(() => {
                            func();
                        }).catch(() => {
                            func();
                        });
                } else {
                    func();
                }
            }, () => {
                this.notificationService.show(this.i18N.tr('error-text') + "!", this.i18N.tr('failed-to-save-text'), NotificationType.Error);
                resolve();
            });
        });
    }

    public isChecked(permissions: Enumerable.IEnumerable<PermissionModel>): boolean {
        return permissions.all(x => x.isChecked);
    }

    public checkAll = ($event, key: string, permissions: Enumerable.IEnumerable<PermissionModel>): void => {
        $event.preventDefault();
        $event.stopPropagation();
        const value = !this.isChecked(permissions);
        const array = permissions.toArray();
        setTimeout(() => {
            array.filter(x => !x.isDisabled).forEach(x => {
                if (x.isChecked != value) {
                    this.assign(x, false);
                }
            });

            setTimeout(() => {
                this.bindingSignaler.signal(key);
            });
        });
    }

    public openPermissionsAuditsDialog() {
        this.dialogClientService.show(DialogServiceConstants.URLs.PermissionsAuditDialog, { id: this.id, entityType: this.permissionEntityType }, true, false, false, "right");
    }

    hideSelectedPermissionsChanged() {
        if(this.hideUnSelectedPermissions && this.hideSelectedPermissions) {
            this.hideUnSelectedPermissions = false;
        }
        else    this.bindingSignaler.signal('filter-permissions');
        
    }

    hideUnSelectedPermissionsChanged() {
        if(this.hideUnSelectedPermissions && this.hideSelectedPermissions) {
            this.hideSelectedPermissions = false;
        }
        else    this.bindingSignaler.signal('filter-permissions');
        
    }
}
